import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, ExternalLink, Search } from "lucide-react";
import { http, formatError } from "@/lib/api";

const PERIODS = [[28, "Last 28 days"], [90, "Last 3 months"], [180, "Last 6 months"], [365, "Last 12 months"]];
const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
const n = (v) => Number(v || 0).toLocaleString("en-GB");

const Setup = ({ data }) => (
  <div className="rounded-2xl bg-white p-6 text-sm leading-relaxed text-slate-700 sm:p-8" data-testid="gsc-setup">
    <h3 className="text-lg font-extrabold text-[#13052A]">{data.reason === "no_access" ? "Almost there: give the website access" : "Connect Google Search Console"}</h3>
    {data.reason === "no_access" ? (
      <p className="mt-3">The connection works, but this account can't see your Search Console property yet. In Search Console, open Settings, then Users and permissions, then Add user, and add <span className="break-all font-bold text-[#581C87]" data-testid="gsc-client-email">{data.client_email}</span> with Full permission. Then press Refresh.</p>
    ) : (
      <p className="mt-3">Search Console isn't connected yet. Once a Google service account key is added to the website and the account is given access to your property, clicks and impressions for each article will appear here.</p>
    )}
  </div>
);

const Stat = ({ label, value, testid }) => (
  <div className="rounded-2xl bg-white p-5" data-testid={testid}>
    <p className="text-xs font-bold uppercase tracking-widest text-[#7C3AED]">{label}</p>
    <p className="mt-2 text-3xl font-extrabold text-[#13052A]">{value}</p>
  </div>
);

const Table = ({ rows }) => (
  <div className="overflow-x-auto rounded-2xl bg-white" data-testid="gsc-table">
    <table className="w-full min-w-[640px] text-left text-sm">
      <thead className="border-b border-violet-100 text-xs uppercase tracking-wider text-slate-500">
        <tr><th className="p-4">Article</th><th className="p-4 text-right">Clicks</th><th className="p-4 text-right">Impressions</th><th className="p-4 text-right">CTR</th><th className="p-4 text-right">Avg position</th></tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.slug} className="border-b border-violet-50 last:border-0" data-testid={`gsc-row-${r.slug}`}>
            <td className="p-4">
              <a href={`/blog/${r.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-start gap-1.5 font-semibold text-[#13052A] hover:text-[#581C87]">{r.title} <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" /></a>
              <p className="text-xs text-slate-500">{r.category}</p>
            </td>
            <td className="p-4 text-right font-bold text-[#13052A]">{n(r.clicks)}</td>
            <td className="p-4 text-right">{n(r.impressions)}</td>
            <td className="p-4 text-right">{r.impressions ? `${(r.ctr * 100).toFixed(1)}%` : "–"}</td>
            <td className="p-4 text-right">{r.position ? r.position.toFixed(1) : "–"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const SearchPerformance = () => {
  const [days, setDays] = useState(28);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = useCallback((refresh = false) => {
    setLoading(true); setError("");
    http.get("/admin/search-console/posts", { params: { days, refresh } })
      .then((r) => setData(r.data)).catch((e) => setError(formatError(e))).finally(() => setLoading(false));
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const t = data?.totals || {};
  return (
    <div data-testid="admin-search-performance">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-extrabold text-[#13052A]"><Search className="h-5 w-5 text-[#7C3AED]" /> Search performance</h2>
          <p className="mt-1 text-sm text-slate-600">Google Search clicks and impressions for each blog article, from Search Console.</p>
        </div>
        <div className="flex gap-2">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="gsc-period" className="rounded-xl border border-violet-200 bg-white px-3 py-2 text-sm font-semibold">
            {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button onClick={() => load(true)} disabled={loading} data-testid="gsc-refresh" className="inline-flex items-center gap-2 rounded-xl bg-[#581C87] px-4 py-2 text-sm font-bold text-white disabled:opacity-60">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>
      <div className="mt-6 space-y-5">
        {loading && !data && <Loader2 className="h-6 w-6 animate-spin text-[#7C3AED]" />}
        {error && <p className="rounded-xl bg-red-50 p-4 text-sm font-semibold text-red-700" data-testid="gsc-error">{error}</p>}
        {data && !data.connected && <Setup data={data} />}
        {data?.connected && (
          <>
            <p className="text-xs text-slate-500" data-testid="gsc-range">{data.site} · {fmtDate(data.start)} to {fmtDate(data.end)} · Google takes about 3 days to finalise data, so the latest days aren't included yet.</p>
            <div className="grid gap-4 sm:grid-cols-3">
              <Stat label="Blog clicks" value={n(t.clicks)} testid="gsc-total-clicks" />
              <Stat label="Blog impressions" value={n(t.impressions)} testid="gsc-total-impressions" />
              <Stat label="Average CTR" value={t.impressions ? `${((t.clicks / t.impressions) * 100).toFixed(1)}%` : "–"} testid="gsc-total-ctr" />
            </div>
            <Table rows={rows} />
          </>
        )}
      </div>
    </div>
  );
};
